import type { Capabilities } from "@/features/account/useCurrentUser";
import type { resources } from "@/i18n/i18n";

export type BuildEntity = "agent" | "data_source" | "skill" | "brand" | "micro_app" | "workflow";

export const BUILD_ENTITIES: readonly BuildEntity[] = [
  "agent",
  "data_source",
  "skill",
  "brand",
  "micro_app",
  "workflow",
];

export const isBuildEntity = (value: string | null): value is BuildEntity =>
  BUILD_ENTITIES.includes(value as BuildEntity);

export interface ExamplePrompt {
  key: keyof (typeof resources)["en"]["buildChat"]["examples"];
  entity: BuildEntity;
  /** Shown only where the deployment has all of these. */
  requires?: readonly (keyof Capabilities)[];
}

/** In display order; within an entity, the first available one is its example on the generic empty state. */
export const EXAMPLE_PROMPTS: readonly ExamplePrompt[] = [
  { key: "agentSupport", entity: "agent" },
  { key: "agentSales", entity: "agent" },
  { key: "dataSourceSharePoint", entity: "data_source", requires: ["sharepointEnabled"] },
  {
    key: "dataSourceSharePointScheduled",
    entity: "data_source",
    requires: ["sharepointEnabled", "automatedSyncEnabled"],
  },
  { key: "dataSourceDrive", entity: "data_source", requires: ["googleDriveEnabled"] },
  { key: "dataSourceWebsite", entity: "data_source" },
  { key: "skillReport", entity: "skill" },
  { key: "brandGuidelines", entity: "brand" },
  { key: "microAppPipeline", entity: "micro_app" },
  { key: "workflowOnboarding", entity: "workflow" },
];

/** One example per entity type, or every example of the entity the user came from. */
export function examplePrompts(capabilities: Capabilities, entity?: BuildEntity | null): ExamplePrompt[] {
  const available = EXAMPLE_PROMPTS.filter((p) => (p.requires ?? []).every((c) => capabilities[c]));
  if (entity) return available.filter((p) => p.entity === entity);
  return BUILD_ENTITIES.flatMap((e) => available.find((p) => p.entity === e) ?? []);
}
